import { eachDayOfInterval, format } from 'date-fns';
import { CALENDAR_START_DATE_FORMAT } from '@/_utils/date';
import { ElementInventoryStatus, ElementType } from '@/redux/slices/slice_types';
import { ApiHourlyData } from '@/components/_common/Table/Calendar/CalendarComponents/types';
import { AvailStatus } from '@/components/_common/Table/Calendar/Calendar.types';
import { capitalizeNoun } from '@/_utils/utils';

export const getAvailStatus = (avail: ApiHourlyData): AvailStatus => {
  if (!avail.elements_total) return AvailStatus.Warning;
  if (avail.element_inventory_status_count.error) return AvailStatus.Error;
  if (avail.element_inventory_status_count.missing) return AvailStatus.Warning;
  return AvailStatus.Success;
};

export const getColorByAvailStatus = (status: AvailStatus, isDarkMode?: boolean): string => {
  switch (status) {
    case AvailStatus.Success:
      return isDarkMode ? 'var(--success-light)' : 'var(--success-dark)';

    case AvailStatus.Warning:
      return isDarkMode ? 'var(--warning-light)' : 'var(--warning-dark)';

    case AvailStatus.Error:
      return isDarkMode ? 'var(--error-light)' : 'var(--error-dark)';

    default:
      return isDarkMode ? 'var(--default-light)' : 'var(--default-dark)';
  }
};

export const getAvailBgColorByStatus = (status: AvailStatus) => {
  if (status === AvailStatus.Success) return '#66EB9233';
  if (status === AvailStatus.Warning) return '#FFA72633';
  return '#E5737333';
};

/**
 * Groups `ApiHourlyData` items by date within the given interval.
 * Creates entries for all dates in the range, including those without any data
 * (these will have an empty array as their value).
 */
export const groupAvailsByDate = (start: Date, end: Date, data: ApiHourlyData[]) => {
  const itemsByDate = eachDayOfInterval({ start, end }).reduce<Record<string, ApiHourlyData[]>>((acc, d) => {
    acc[format(d, CALENDAR_START_DATE_FORMAT)] = [];
    return acc;
  }, {});

  data.forEach((item) => {
    const date = format(new Date(item.term_start), CALENDAR_START_DATE_FORMAT);
    if (itemsByDate[date]) {
      itemsByDate[date].push(item);
    }
  });

  return itemsByDate;
};

const typeToLabelMap: Partial<Record<ElementType, string>> = {
  timedtext: 'Timed Text'
};

export const getElementTypeLabel = (elementType: ElementType) => {
  return typeToLabelMap[elementType] ?? capitalizeNoun(elementType);
};

export type StatusColor = 'success' | 'warning' | 'error';

export const ElementStatusColorsOrdered: StatusColor[] = ['success', 'warning', 'error'];

export const getColorByElementStatus = (status: ElementInventoryStatus): StatusColor => {
  switch (status) {
    case ElementInventoryStatus.Available:
      return 'success';
    case ElementInventoryStatus.Pending:
    case ElementInventoryStatus.InProgress:
    case ElementInventoryStatus.Unavailable:
    case ElementInventoryStatus.Missing:
      return 'warning';
    case ElementInventoryStatus.Error:
      return 'error';
    default:
      return 'warning';
  }
};

export const getTileBgColorByElStatus = (inventoryStatus: ElementInventoryStatus, isDarkMode: boolean) => {
  switch (inventoryStatus) {
    case ElementInventoryStatus.Available:
      return isDarkMode ? '#16251F' : '#d9fff1';
    case ElementInventoryStatus.Error:
      return isDarkMode ? '#321718' : '#ffcfd0';
    case ElementInventoryStatus.Missing:
    case ElementInventoryStatus.Pending:
    case ElementInventoryStatus.InProgress:
    case ElementInventoryStatus.Unavailable:
    default:
      return isDarkMode ? '#261E14' : '#ffebd4';
  }
};

export const getTileIconBgColorByElStatus = (inventoryStatus: ElementInventoryStatus, isDarkMode: boolean) => {
  switch (inventoryStatus) {
    case ElementInventoryStatus.Available:
      return isDarkMode ? '#66EB921A' : 'rgba(101,235,145,0.2)';
    case ElementInventoryStatus.Error:
      return isDarkMode ? '#F443361A' : 'rgba(255,69,56,0.2)';
    case ElementInventoryStatus.Missing:
    case ElementInventoryStatus.Pending:
    case ElementInventoryStatus.InProgress:
    case ElementInventoryStatus.Unavailable:
    default:
      return isDarkMode ? '#FFA7261A' : 'rgba(255,168,38,0.2)';
  }
};
